import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {ExclusionListDto} from "../../model/dto/entity/exclusion-list.dto";
import {ExclusionListEntryDto} from "../../model/dto/entity/exclusion-list-entry.dto";
import {AddToExclusionListRequest, ExclusionListActionResponse} from "../../model/dto/api/exclusion-list.interface";

/**
 * Service for managing exclusion lists.
 * F-022: Member Exclusion Lists - Provides API operations for managing global
 * and campaign-specific exclusion lists.
 */
@Injectable({
    providedIn: 'root'
})
export class ExclusionListService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/exclusionLists')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-user-slash';
    }

    /**
     * Gets all active exclusion lists.
     */
    getActiveExclusionLists(): Observable<ExclusionListDto[]> {
        return this.getGeneric<ExclusionListDto[]>('/active');
    }

    /**
     * Gets all global exclusion lists.
     */
    getGlobalExclusionLists(): Observable<ExclusionListDto[]> {
        return this.getGeneric<ExclusionListDto[]>('/global');
    }

    /**
     * Gets exclusion lists by type.
     */
    getExclusionListsByType(listType: string): Observable<ExclusionListDto[]> {
        return this.getGeneric<ExclusionListDto[]>(`/byType/${listType}`);
    }

    /**
     * Activates an exclusion list.
     */
    activateExclusionList(exclusionListId: number): Observable<ExclusionListActionResponse> {
        return this.postGeneric<ExclusionListActionResponse>({}, `/${exclusionListId}/activate`);
    }

    /**
     * Deactivates an exclusion list.
     */
    deactivateExclusionList(exclusionListId: number): Observable<ExclusionListActionResponse> {
        return this.postGeneric<ExclusionListActionResponse>({}, `/${exclusionListId}/deactivate`);
    }

    /**
     * Adds entries to an exclusion list.
     * F-022: Acceptance Criteria - Given a record is added to exclusion list,
     * when rolling campaigns refresh, then record is removed from membership.
     */
    addEntriesToExclusionList(exclusionListId: number, request: AddToExclusionListRequest): Observable<ExclusionListActionResponse> {
        return this.postGeneric<ExclusionListActionResponse>(request, `/${exclusionListId}/entries`);
    }

    /**
     * Removes a customer from an exclusion list.
     */
    removeCustomerFromExclusionList(exclusionListId: number, customerId: number): Observable<ExclusionListActionResponse> {
        return this.http.delete<ExclusionListActionResponse>(
            `${MvsCrudService.baseUrl}/ct/exclusionLists/${exclusionListId}/entries/customer/${customerId}`
        );
    }

    /**
     * Removes a contract from an exclusion list.
     */
    removeContractFromExclusionList(exclusionListId: number, contractId: number): Observable<ExclusionListActionResponse> {
        return this.http.delete<ExclusionListActionResponse>(
            `${MvsCrudService.baseUrl}/ct/exclusionLists/${exclusionListId}/entries/contract/${contractId}`
        );
    }

    /**
     * Gets all entries for an exclusion list.
     * F-022: Acceptance Criteria - Given exclusion list, when viewing,
     * then exclusion reason and date are visible.
     */
    getExclusionListEntries(exclusionListId: number): Observable<ExclusionListEntryDto[]> {
        return this.getGeneric<ExclusionListEntryDto[]>(`/${exclusionListId}/entries`);
    }

    /**
     * Gets active entries for an exclusion list.
     */
    getActiveExclusionListEntries(exclusionListId: number): Observable<ExclusionListEntryDto[]> {
        return this.getGeneric<ExclusionListEntryDto[]>(`/${exclusionListId}/entries/active`);
    }

    /**
     * Gets the count of campaigns using this exclusion list.
     */
    getCampaignCountForExclusionList(exclusionListId: number): Observable<number> {
        return this.getGeneric<number>(`/${exclusionListId}/campaign-count`);
    }

    /**
     * Checks if a customer is in an exclusion list.
     */
    isCustomerInExclusionList(exclusionListId: number, customerId: number): Observable<boolean> {
        return this.getGeneric<boolean>(`/${exclusionListId}/check/customer/${customerId}`);
    }

    /**
     * Checks if a contract is in an exclusion list.
     */
    isContractInExclusionList(exclusionListId: number, contractId: number): Observable<boolean> {
        return this.getGeneric<boolean>(`/${exclusionListId}/check/contract/${contractId}`);
    }
}
